import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("auth_token"));

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      // No token — ensure admin exists (one-time init)
      axios.post(`${API}/auth/init-admin`).catch(() => {});
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyToken = async (retries = 1) => {
    try {
      const response = await axios.post(`${API}/auth/verify?token=${token}`);
      setUser(response.data.user);
    } catch (error) {
      const status = error.response?.status;
      if (status === 401) {
        // Token genuinely invalid or expired — clear it
        localStorage.removeItem("auth_token");
        setToken(null);
        setUser(null);
      } else if (retries > 0) {
        // Network error / timeout — retry once
        await new Promise(r => setTimeout(r, 500));
        return verifyToken(retries - 1);
      }
      // If retry also failed with non-401, keep token and let user stay
      // They'll get a fresh verify on next action
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, { username, password });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem("auth_token", newToken);
    setToken(newToken);
    setUser(userData);

    if (userData.is_wholesale) {
      window.location.href = '/wholesale';
      return userData;
    }
    
    return userData;
  };

  const logout = async () => {
    if (token) {
      try {
        await axios.post(`${API}/auth/logout?token=${token}`);
      } catch (error) {
        // Ignore logout errors
      }
    }
    
    localStorage.removeItem("auth_token");
    setToken(null);
    setUser(null);
  };

  const changePassword = async (currentPassword, newPassword) => {
    await axios.put(`${API}/auth/change-password?token=${token}`, null, {
      params: { current_password: currentPassword, new_password: newPassword }
    });
  };

  const hasPermission = (moduleId) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (user.permissions?.includes("all")) return true;
    return user.permissions?.includes(moduleId) || false;
  };

  const isAdmin = () => {
    return user?.role === "admin";
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token,
      loading, 
      login, 
      logout, 
      changePassword,
      hasPermission,
      isAdmin
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
